import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Clock, Users, Play, Pause, RotateCcw, CheckCircle, XCircle, BookOpen } from 'lucide-react';
import { scenarios } from '../data/scenarios';
import { useClass } from '../context/ClassContext';

export default function ScenarioDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const scenario = scenarios.find(s => s.id === id);
  const { currentClass } = useClass();
  
  const [isRunning, setIsRunning] = useState(false);
  const [time, setTime] = useState(0);
  const [evaluation, setEvaluation] = useState<Record<string, boolean>>({});
  const [selectedStudent, setSelectedStudent] = useState<string | null>(null);

  // Trouver automatiquement l'élève assigné à ce scénario
  useEffect(() => {
    if (currentClass && scenario) {
      const assignedStudent = currentClass.students.find(
        student => student.assignedScenario === scenario.id
      );
      if (assignedStudent) {
        setSelectedStudent(assignedStudent.id);
      }
    }
  }, [currentClass, scenario]);

  useEffect(() => {
    if (scenario) {
      setTime(scenario.duree * 60);
    }
  }, [scenario]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isRunning && time > 0) {
      interval = setInterval(() => {
        setTime(prevTime => {
          if (prevTime <= 1) {
            setIsRunning(false);
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isRunning, time]);

  if (!scenario) {
    return (
      <div className="min-h-screen bg-gray-50 px-4 py-12">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-gray-900">Scénario non trouvé</h2>
          <button
            onClick={() => navigate('/scenarios')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Retour aux scénarios
          </button>
        </div>
      </div>
    );
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const toggleTimer = () => {
    setIsRunning(!isRunning);
  };

  const resetTimer = () => {
    setIsRunning(false);
    setTime(scenario.duree * 60);
  };

  const toggleEvaluation = (criterion: string) => {
    setEvaluation(prev => ({
      ...prev,
      [criterion]: !prev[criterion]
    }));
  };

  const getNiveauColor = (niveau: string) => {
    switch (niveau) {
      case 'Débutant':
        return 'bg-green-100 text-green-800';
      case 'Intermédiaire':
        return 'bg-yellow-100 text-yellow-800';
      case 'Avancé':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'Médical':
        return 'bg-blue-100 text-blue-800';
      case 'Traumatique':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getAssignedStudentName = () => {
    if (!currentClass || !selectedStudent) return null;
    const student = currentClass.students.find(s => s.id === selectedStudent);
    return student ? `${student.firstName} ${student.lastName}` : null;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* En-tête */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{scenario.titre}</h1>
                <div className="mt-2 flex items-center space-x-4">
                  <span className={`px-2.5 py-0.5 rounded-full text-sm font-medium ${getTypeColor(scenario.type)}`}>
                    {scenario.type}
                  </span>
                  <span className={`px-2.5 py-0.5 rounded-full text-sm font-medium ${getNiveauColor(scenario.niveau)}`}>
                    {scenario.niveau}
                  </span>
                  <div className="flex items-center text-gray-500">
                    <Clock className="h-4 w-4 mr-1" />
                    {scenario.duree} min
                  </div>
                  <div className="flex items-center text-gray-500">
                    <Users className="h-4 w-4 mr-1" />
                    {scenario.participants} pers.
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Affichage de l'élève assigné */}
          {getAssignedStudentName() && (
            <div className="px-6 py-4 bg-blue-50 border-b border-blue-100">
              <div className="flex items-center">
                <Users className="h-5 w-5 text-blue-600 mr-2" />
                <span className="font-medium text-blue-900">
                  Élève assigné: {getAssignedStudentName()}
                </span>
              </div>
            </div>
          )}

          {/* Historique du scénario */}
          <div className="px-6 py-4 bg-blue-50 border-b border-blue-100">
            <div className="flex items-start space-x-3">
              <BookOpen className="h-5 w-5 text-blue-600 mt-1" />
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Contexte</h3>
                <p className="text-gray-700">{scenario.historique}</p>
              </div>
            </div>
          </div>

          {/* Chronomètre */}
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center justify-center space-x-4">
              <div className="text-4xl font-mono font-bold text-gray-900">
                {formatTime(time)}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={toggleTimer}
                  className={`p-2 rounded-full ${
                    isRunning ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'
                  } hover:opacity-80`}
                >
                  {isRunning ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6" />}
                </button>
                <button
                  onClick={resetTimer}
                  className="p-2 rounded-full bg-gray-100 text-gray-600 hover:opacity-80"
                >
                  <RotateCcw className="h-6 w-6" />
                </button>
              </div>
            </div>
          </div>

          {/* Contenu */}
          <div className="px-6 py-4 grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Description et objectifs */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Description</h2>
              <p className="text-gray-600 mb-6">{scenario.description}</p>
              
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Objectifs</h2>
              <ul className="list-disc list-inside space-y-2 text-gray-600">
                {scenario.objectifs?.map((objectif, index) => (
                  <li key={index}>{objectif}</li>
                ))}
              </ul>
            </div>

            {/* Grille d'évaluation */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Grille d'évaluation</h2>
              <div className="space-y-4">
                {scenario.evaluation?.map((criterion, index) => (
                  <div
                    key={index}
                    className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg cursor-pointer"
                    onClick={() => selectedStudent && toggleEvaluation(criterion)}
                  >
                    {evaluation[criterion] ? (
                      <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
                    ) : (
                      <XCircle className="h-6 w-6 text-gray-400 flex-shrink-0" />
                    )}
                    <span className="text-gray-700">{criterion}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Matériel et déroulement */}
          <div className="px-6 py-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Matériel nécessaire</h2>
                <ul className="list-disc list-inside space-y-2 text-gray-600">
                  {scenario.materiel?.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Déroulement</h2>
                <ol className="list-decimal list-inside space-y-2 text-gray-600">
                  {scenario.deroulement?.map((etape, index) => (
                    <li key={index}>{etape}</li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}